import React from 'react';
import { observer } from 'mobx-react';
import { reverse } from 'named-urls';

import { EntityModules, RegisterEntityModule } from 'core/constants';
import { GridEventListener, GridRowParams } from '@mui/x-data-grid';

import { useParams, useLocation } from 'react-router-dom';

import Record from 'ui/Record/Record';

import MainHeader from 'components/MainHeader';

import routes from 'core/routes';
import { useStore } from 'utils/hooks/store';

import Grid from 'components/Grid';

import { useNavBarHeight } from 'utils/hooks/navbar';

import { tasksTableBasicColumns } from 'containers/Tasks/components/TasksTable/TasksTable';

import { getRelatedEntityDetails } from '../helper';
import { auditTasksPageColumns } from './utils';

const RelatedActions: React.FunctionComponent = () => {
  const { routing } = useStore();
  const navBarHeight = useNavBarHeight();

  const onRowClick: GridEventListener<'rowClick'> = (task: GridRowParams): void => {
    routing.push(reverse(routes.dashboard.tasks.details, { taskId: task.row.uuid }));
  };

  const params = useParams<{ entityId: string }>();
  const location = useLocation();
  const { entityId } = params;

  const {
    store: { loadDetails, details, path, detailsPath },
    translations,
    nameKey,
    entityType,
  } = getRelatedEntityDetails(location.pathname);

  const isAudit = entityType === EntityModules.Audits;

  React.useEffect(() => {
    const fetchDetails = async (): Promise<void> => {
      loadDetails(entityId as string);
    };

    fetchDetails();
  }, [entityId]);

  return (
    <Record
      Header={
        <MainHeader
          title={`Related Actions to ${details?.entity[nameKey].value || ''}`}
          breadcrumbs={[
            { label: translations.plural, link: path },
            { label: 'Details', link: detailsPath(entityId as string) },
            {
              label: 'Related Actions',
              link: isAudit
                ? reverse(routes.dashboard.audits.relatedActions, { entityId: entityId as string })
                : reverse(routes.dashboard[entityType as RegisterEntityModule].relatedActions, {
                    entityId: entityId as string,
                  }),
            },
          ]}
        />
      }
      Content={(onError) => (
        <Grid
          basicColumns={isAudit ? auditTasksPageColumns : tasksTableBasicColumns}
          entity="action"
          onError={onError}
          onRowClick={onRowClick}
          dataURL={
            isAudit ? `/api/entities/${EntityModules.Audits}/${entityId}/tasks/` : `/api/tasks/?entity=${entityId}`
          }
          // No ordering or filtering on this endpoint, and the record's card shares the dataURL.
          sortBy={isAudit ? undefined : { field: 'dueDate', sort: 'asc' }}
          showQuickFilter={!isAudit}
          disableColumnFilter={isAudit}
          disableColumnSorting={isAudit}
          disableColumnSelector={isAudit}
          disableSavePreferences={isAudit}
          onlyBasicExport
          customNoRowsText={isAudit ? 'No actions linked to this audit' : 'No tasks have been created yet'}
        />
      )}
      navBarHeight={navBarHeight}
    />
  );
};

export default observer(RelatedActions);
